import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Truck, AlertTriangle } from "lucide-react";
import { fmtKg, needsFlatbed, haulageLink, FLATBED_T } from "@/lib/weight";

export const QTY_MIN = 0;
export const QTY_MAX = 99;
export const clampQty = (v) => { const n = Math.trunc(Number(v)); if (!Number.isFinite(n)) return 0; return Math.min(QTY_MAX, Math.max(QTY_MIN, n)); };

/** Universal selector: 0–99, whole numbers, −/+ exactly one, buttons disabled at the bounds. 0 = nothing selected. */
export function QtyStepper({ value, onChange, min = QTY_MIN, max = QTY_MAX, size = "md", id = "qty", unit }) {
  const v = clampQty(value);
  const [hint, setHint] = useState("");
  const set = (n) => { const c = Math.min(max, Math.max(min, Math.trunc(n))); setHint(c >= max && n >= max ? `Maximum quantity of ${max} items reached.` : ""); onChange(c); };
  const pad = size === "sm" ? "px-2.5 py-1.5" : "px-3.5 py-3";
  const atMin = v <= min, atMax = v >= max;
  return (
    <div className="inline-flex flex-col items-start gap-1" data-testid={`${id}-stepper`}>
      <div className={`inline-flex items-center border border-border rounded-xl bg-background ${size === "sm" ? "text-xs" : ""}`} role="group" aria-label="Quantity">
        <button type="button" onClick={() => set(v - 1)} disabled={atMin} className={`${pad} disabled:opacity-30 disabled:cursor-not-allowed`} aria-label="Decrease quantity by one" data-testid={`${id}-minus`}><Minus size={size === "sm" ? 12 : 16} /></button>
        <input type="number" inputMode="numeric" step="1" min={min} max={max} value={v} onChange={(e) => set(e.target.value === "" ? 0 : Number(e.target.value))} onKeyDown={(e) => { if ([".", ",", "-", "e", "E", "+"].includes(e.key)) e.preventDefault(); }} className={`text-center font-semibold bg-transparent outline-none ${size === "sm" ? "w-12" : "w-16"} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`} aria-label={`Quantity${unit ? ` in ${unit}` : ""}, ${min} to ${max}`} aria-valuemin={min} aria-valuemax={max} aria-valuenow={v} data-testid={`${id}-input`} />
        <button type="button" onClick={() => set(v + 1)} disabled={atMax} className={`${pad} disabled:opacity-30 disabled:cursor-not-allowed`} aria-label="Increase quantity by one" data-testid={`${id}-plus`}><Plus size={size === "sm" ? 12 : 16} /></button>
      </div>
      {hint && <span className="text-[11px] text-amber-700" role="status" data-testid={`${id}-max-hint`}>{hint}</span>}
    </div>
  );
}

export function WeightNote({ unitKg, qty, unit, id = "weight", compact = false }) {
  if (unitKg == null) return null;
  const total = unitKg * (Number(qty) || 0);
  return (
    <div className={`${compact ? "text-[11px]" : "text-xs"} text-muted-foreground`} data-testid={`${id}-note`}>
      <span>Unit weight <b className="text-foreground">{fmtKg(unitKg)}</b>{unit ? ` / ${unit}` : ""}</span> · <span>Total weight <b className="text-foreground" data-testid={`${id}-total`}>{fmtKg(total)}</b></span>
      {needsFlatbed(total) && <FlatbedAlert kg={total} compact />}
    </div>
  );
}

export function FlatbedAlert({ kg, compact = false, desc }) {
  if (!needsFlatbed(kg)) return null;
  return (
    <div className={`${compact ? "mt-1 p-2 text-[11px]" : "mt-3 p-3 text-xs"} rounded-xl bg-red-50 border border-red-200 text-red-800 flex gap-2 items-start`} data-testid="flatbed-required">
      <AlertTriangle size={compact ? 12 : 14} className="shrink-0 mt-0.5" />
      <div>
        <b>{fmtKg(kg)} — {FLATBED_T} t reached. A flatbed is required for this load.</b>{!compact && " Standard delivery can't carry it; request a flatbed before you place the order."}
        <Link to={haulageLink(kg, desc)} className="ml-1 underline font-semibold inline-flex items-center gap-1" data-testid="flatbed-request-link"><Truck size={12} /> Request a flatbed</Link>
      </div>
    </div>
  );
}
